import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type AnalyticsSummary } from "../lib/api";

function SparkBar({ data, maxCount }: { data: { date: string; count: number }[]; maxCount: number }) {
  if (!data.length) {
    return <div style={{ color: "var(--muted)", fontSize: "0.85rem" }}>No data yet</div>;
  }
  return (
    <div style={{ display: "flex", gap: "2px", height: "60px", alignItems: "flex-end" }}>
      {data.map((d) => (
        <div
          key={d.date}
          title={`${d.date}: ${d.count}`}
          style={{
            flex: 1,
            background: "var(--accent, #10b981)",
            borderRadius: "2px 2px 0 0",
            height: maxCount > 0 ? `${Math.round((d.count / maxCount) * 100)}%` : "0%",
            minHeight: d.count > 0 ? 4 : 0,
            transition: "height 200ms",
          }}
        />
      ))}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: number | string }) {
  return (
    <div
      className="card"
      style={{ textAlign: "center", padding: "1.25rem 1rem" }}
    >
      <div style={{ fontSize: "2rem", fontWeight: 700, color: "var(--accent, #10b981)" }}>
        {value}
      </div>
      <div style={{ color: "var(--muted)", fontSize: "0.85rem", marginTop: "0.25rem" }}>
        {label}
      </div>
    </div>
  );
}

export function AnalyticsPage() {
  const { t } = useTranslation();
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .getAnalyticsSummary()
      .then(setSummary)
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load analytics"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <p style={{ color: "var(--muted)" }}>{t("analytics.loading")}</p>;
  }
  if (error) {
    return <div className="alert alert-error">{error}</div>;
  }
  if (!summary) {
    return null;
  }

  const topGalleries = summary.top_galleries ?? [];

  return (
    <div>
      <h1 style={{ marginTop: 0 }}>{t("analytics.title")}</h1>
      <p style={{ color: "var(--muted)", marginTop: "-0.5rem", marginBottom: "1.5rem" }}>
        Last 30 days
      </p>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: "1rem",
          marginBottom: "2rem",
        }}
      >
        <StatCard label="Gallery views" value={summary.gallery_views_30d} />
        <StatCard label="Unique viewers" value={summary.unique_gallery_viewers_30d} />
        <StatCard label="Downloads" value={summary.asset_downloads_30d} />
        <StatCard label="Leads" value={summary.leads_30d} />
      </div>

      <h2 style={{ fontSize: "1.1rem", marginBottom: "0.75rem" }}>Top galleries</h2>
      {topGalleries.length === 0 ? (
        <div className="card">
          <p style={{ margin: 0, color: "var(--muted)" }}>No gallery data yet. Share your galleries to start tracking views.</p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--border, #2c3640)" }}>
                <th style={{ textAlign: "left", padding: "0.75rem 1rem", fontWeight: 600, color: "var(--muted)" }}>Gallery</th>
                <th style={{ textAlign: "right", padding: "0.75rem 1rem", fontWeight: 600, color: "var(--muted)" }}>Views</th>
                <th style={{ textAlign: "right", padding: "0.75rem 1rem", fontWeight: 600, color: "var(--muted)" }}>Downloads</th>
              </tr>
            </thead>
            <tbody>
              {topGalleries.map((g, i) => (
                <tr
                  key={g.slug}
                  style={{
                    borderBottom: i < topGalleries.length - 1 ? "1px solid var(--border, #2c3640)" : undefined,
                  }}
                >
                  <td style={{ padding: "0.75rem 1rem" }}>
                    <a href={`#/galleries/${encodeURIComponent(g.slug)}`} style={{ color: "var(--text)" }}>
                      {g.title}
                    </a>
                  </td>
                  <td style={{ textAlign: "right", padding: "0.75rem 1rem", color: "var(--accent, #10b981)", fontWeight: 600 }}>
                    {g.views}
                  </td>
                  <td style={{ textAlign: "right", padding: "0.75rem 1rem", color: "var(--muted)" }}>
                    {g.downloads}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {topGalleries.length > 0 && (
        <GalleryStatsSection topSlug={topGalleries[0].slug} />
      )}
    </div>
  );
}

function GalleryStatsSection({ topSlug }: { topSlug: string }) {
  const [analytics, setAnalytics] = useState<import("../lib/api").GalleryAnalytics | null>(null);

  useEffect(() => {
    api.getGalleryAnalytics(topSlug).then(setAnalytics).catch(() => {});
  }, [topSlug]);

  if (!analytics) return null;

  const maxViews = Math.max(...(analytics.views_by_day.map((d) => d.count)), 1);

  return (
    <div style={{ marginTop: "2rem" }}>
      <h2 style={{ fontSize: "1.1rem", marginBottom: "0.75rem" }}>
        Views trend — <a href={`#/galleries/${encodeURIComponent(topSlug)}`}>{topSlug}</a>
      </h2>
      <div className="card">
        <p style={{ margin: "0 0 0.75rem", fontSize: "0.85rem", color: "var(--muted)" }}>
          {analytics.total_views} views · {analytics.unique_viewers} unique viewers (30d)
        </p>
        <SparkBar data={analytics.views_by_day} maxCount={maxViews} />
      </div>
    </div>
  );
}
